import { DatePicker } from 'antd';
import moment from 'moment';
import {
  cloneElement,
  CSSProperties,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import getFormatMessage from '../../locales/get-format-message';
import { CommonAddButton, MainButton } from '../button/main';
import { CommonInput, CommonSearchInput } from '../input';
import { CommonRadioButton } from '../radio/radio-button';
import { CommonSelect } from '../select';
import { CommonCheckBoxSelect } from '../select/checkbox-select';

import styles from './index.module.scss';

export interface IFilterItem<T> {
  style?: CSSProperties;
  className?: string;
  render?: (commonProps?: any) => ReactNode;
  type?:
    | 'search'
    | 'text'
    | 'select'
    | 'multi-select'
    | 'add-btn'
    | 'date-range'
    | 'clear-btn'
    | 'radio-btn-group';
  disableFilter?: boolean;
  label?: ReactNode;
  placeholder?: string;
  name: string;
  props?: Record<string, any>;
  filter?: (val: any, record: T) => Boolean;
  place?: 'left' | 'right';
  defaultValue?: any;
  options?: {
    label: ReactNode;
    value: any;
  }[];
  onClick?: () => void;
}

interface IProps<T = Record<string, any>, P = Record<string, any>> {
  hiddenFields?: string[];
  values?: P;
  children?: ReactNode;
  filterItems?: {
    list?: readonly T[];
    items: readonly IFilterItem<T>[];
  };
  className?: string;
  style?: CSSProperties;
  onFilterChange?: (values: P, changeFiled: string, filterList: T[]) => void;
}

export const FilterGroup: <T, P>(props: IProps<T, P>) => JSX.Element = (
  props
) => {
  const { onFilterChange, hiddenFields, values, filterItems } = props;
  const [editValues, setEditValues] = useState<Record<string, any>>(
    props.values || {}
  );

  useEffect(() => {
    setEditValues(values || {});
  }, [values]);

  useEffect(() => {
    noticeFilterChange();
  }, [filterItems?.list]);

  const getValue = (name = '') => {
    if (values) {
      // @ts-ignore
      return values[name];
    }
    return editValues[name];
  };

  const onFieldChange = (name = '', val: any) => {
    const newValues = { ...editValues };
    if (val && val instanceof Object && 'target' in val) {
      newValues[name] = val?.target?.value;
    } else {
      newValues[name] = val;
    }
    setEditValues(newValues);
    noticeFilterChange(newValues, name);
  };

  const noticeFilterChange = (newValues = editValues, name = '') => {
    if (!filterItems) {
      return;
    }
    const filterValues = { ...newValues };
    const getFilterList = () => {
      return filterItems.list?.filter((listItem) => {
        const record = listItem as Record<string, any>;
        const filterNames = Object.keys(filterValues);
        return (
          !filterNames.length ||
          filterNames.every((filterName) => {
            const filterConfig = filterItems.items?.find(
              (item) => item.name === filterName
            );
            const filterValue = filterValues[filterName];
            if (
              !filterConfig ||
              filterValue === undefined ||
              filterValue === '' ||
              filterValue === null ||
              filterValue?.length === 0
            ) {
              return true;
            }
            if (filterConfig.disableFilter) {
              return true;
            }
            if (filterConfig.filter) {
              return filterConfig.filter(filterValue, record as any);
            }

            if (filterConfig.type === 'date-range') {
              return (
                moment(record[filterName]).isSameOrBefore(filterValue[1]) &&
                moment(record[filterName]).isSameOrAfter(filterValue[0])
              );
            }

            if (filterConfig.type === 'select') {
              return record[filterName] === filterValue;
            }
            if (filterConfig.type === 'multi-select') {
              return (
                ((filterValue || []) as Array<any>).indexOf(
                  record[filterName]
                ) >= 0
              );
            }
            return (String(record[filterName] || '') as string)
              .toLowerCase()
              .includes((filterValue || '').toLowerCase());
          })
        );
      });
    };
    const filterList = getFilterList();
    // @ts-ignore
    onFilterChange?.(filterValues, name, filterList);
  };

  const renderFilterItem = <T,>(filterItem: IFilterItem<T>) => {
    const commonProps = {
      style: filterItem.style,
      className: filterItem.className,
      value: getValue(filterItem.name),
      placeholder: filterItem.placeholder,
      defaultValue: filterItem.defaultValue,
      onChange: (val: any) => onFieldChange(filterItem.name, val),
    };
    if (filterItem.render) {
      return filterItem.render(commonProps);
    }
    if (!filterItem.type || filterItem.type === 'text') {
      return <CommonInput {...commonProps} prefix={filterItem.label} />;
    }
    if (filterItem.type === 'search') {
      return <CommonSearchInput {...commonProps} allowClear />;
    }
    if (filterItem.type === 'select') {
      return (
        <CommonSelect
          {...commonProps}
          options={filterItem.options}
          prefix={filterItem.label as string}
          style={{ width: 'auto', minWidth: 100 }}
          allowClear
          dropdownMatchSelectWidth={false}
          {...(filterItem.props || {})}
        />
      );
    }
    if (filterItem.type === 'multi-select') {
      return (
        <CommonCheckBoxSelect
          {...commonProps}
          options={filterItem.options}
          prefix={filterItem.label as string}
          mode="multiple"
          allowClear
          dropdownMatchSelectWidth={false}
          maxCount={1}
          {...(filterItem.props || {})}
        />
      );
    }

    if (filterItem.type === 'add-btn') {
      return (
        <CommonAddButton {...commonProps} onClick={filterItem.onClick}>
          {filterItem.label}
        </CommonAddButton>
      );
    }

    if (filterItem.type === 'clear-btn') {
      return (
        <MainButton
          id="auto-filter-clear"
          {...commonProps}
          onClick={() => {
            const resetValues = (filterItems?.items || [])
              .filter((item) => item.disableFilter)
              .reduce<Record<string, any>>((acc, item) => {
                acc[item.name] = editValues[item.name];
                return acc;
              }, {});
            setEditValues(resetValues);
            if (
              Object.keys(editValues).some((filterKey) => {
                return (
                  editValues[filterKey] !== '' &&
                  editValues[filterKey] !== undefined &&
                  editValues[filterKey] !== null
                );
              })
            ) {
              noticeFilterChange(resetValues);
            }

            filterItem.onClick?.();
          }}
        >
          {filterItem.label || getFormatMessage('clear_all')}
        </MainButton>
      );
    }

    if (filterItem.type === 'date-range') {
      return (
        <DatePicker.RangePicker
          style={commonProps.style}
          className={commonProps.className}
          onChange={commonProps.onChange}
          value={commonProps.value || commonProps.defaultValue}
        />
      );
    }

    if (filterItem.type === 'radio-btn-group') {
      return (
        <CommonRadioButton
          optionType="button"
          value={commonProps.value || filterItem.defaultValue}
          options={filterItem.options || []}
          onChange={commonProps.onChange}
        />
      );
    }

    return null;
  };

  if (filterItems) {
    return (
      <div
        {...props}
        className={`${styles.filterGroup} ${props.className || ''}`}
      >
        <div className={styles.left}>
          {filterItems.items
            .filter((item) => item.place === 'left' || !item.place)
            .map((filterItem) => {
              return renderFilterItem(filterItem);
            })}
        </div>
        <div className={styles.right}>
          {filterItems.items
            .filter((item) => item.place === 'right')
            .map((filterItem) => {
              return renderFilterItem(filterItem);
            })}
        </div>
      </div>
    );
  }

  if (props.children instanceof Array) {
    return (
      <div
        {...props}
        className={`${styles.filterGroup} ${props.className || ''}`}
      >
        {props.children

          .filter((child) => child && !hiddenFields?.includes(child.props.name))
          .map((child) => {
            const getElementValue = () => {
              if (!child.props.name) {
                return;
              }
              return getValue(child.props.name);
            };
            return cloneElement(child, {
              onChange: (val: any) => {
                child.props.onChange?.(val);
                if (!child.props.name) {
                  return;
                }
                if (val && val instanceof Object && 'target' in val) {
                  editValues[child.props.name] = val?.target?.value;
                } else {
                  editValues[child.props.name] = val;
                }
                // @ts-ignore
                onFilterChange?.({ ...editValues }, child.props.name);
              },
              value: getElementValue(),
            });
          })}
      </div>
    );
  }
  return (
    <div
      {...props}
      className={`${styles.filterGroup} ${props.className || ''}`}
    >
      {props.children}
    </div>
  );
};
